/**
 * Ingreso Firebase Repository - Repositorio real para ingresos en Firestore
 */

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  onSnapshot, 
  Timestamp,
  type DocumentData 
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import type { Ingreso } from '../../../shared/types';
import { LoggerService } from '../../../shared/services/logger.service';

export class IngresoFirebaseRepository {
  private readonly collectionName = 'ingresos';

  /**
   * Suscripción en tiempo real a los ingresos del usuario
   */
  subscribeToIngresos(userId: string, callback: (ingresos: Ingreso[]) => void): () => void {
    LoggerService.info('Subscribiendo a ingresos en tiempo real', 'IngresoFirebaseRepository', { userId });

    const ingresosQuery = query(
      collection(db, this.collectionName),
      where('userId', '==', userId),
      orderBy('fecha', 'desc')
    );
    


    const unsubscribe = onSnapshot(
      ingresosQuery,
      (snapshot) => {

        
        const ingresos: Ingreso[] = snapshot.docs.map(doc => {
          const data = doc.data() as DocumentData;
          return {
            id: doc.id,
            userId: data.userId,
            concepto: data.concepto || data.descripcion, // Compatibilidad con datos anteriores
            monto: data.monto,
            fecha: data.fecha instanceof Timestamp ? data.fecha.toDate() : new Date(data.fecha),
            categoria: data.categoria,
            descripcion: data.descripcion || data.concepto,
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
            updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(data.updatedAt)
          };
        });

        LoggerService.info('Ingresos actualizados desde Firebase', 'IngresoFirebaseRepository', { 
          userId, 
          count: ingresos.length 
        });

        callback(ingresos);
      },
      (error) => {
        console.error('🚨 ERROR Firebase Firestore:', {
          error: error.message,
          code: error.code,
          userId,
          collection: this.collectionName
        });
        
        LoggerService.error('Error al suscribirse a ingresos', 'IngresoFirebaseRepository', { 
          userId, 
          error: error.message,
          code: error.code
        });
        

        
        callback([]); // Retornar array vacío en caso de error
      }
    );

    return unsubscribe;
  }

  /**
   * Crear un nuevo ingreso
   */
  async createIngreso(userId: string, ingresoData: Omit<Ingreso, 'id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      LoggerService.info('Creando nuevo ingreso', 'IngresoFirebaseRepository', { userId, ingresoData });

      const now = new Date();
      const docData = {
        userId,
        concepto: ingresoData.concepto,
        monto: ingresoData.monto,
        fecha: Timestamp.fromDate(ingresoData.fecha),
        categoria: ingresoData.categoria,
        descripcion: ingresoData.descripcion || ingresoData.concepto,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now)
      };

      const docRef = await addDoc(collection(db, this.collectionName), docData);

      LoggerService.logUserAction('ingreso_created', 'IngresoFirebaseRepository', { 
        userId, 
        ingresoId: docRef.id,
        monto: ingresoData.monto,
        categoria: ingresoData.categoria
      });

      return docRef.id;
    } catch (error) {
      LoggerService.error('Error al crear ingreso', 'IngresoFirebaseRepository', { 
        userId, 
        error: error instanceof Error ? error.message : 'Error desconocido'
      });
      throw error;
    }
  }

  /**
   * Actualizar un ingreso existente
   */
  async updateIngreso(userId: string, id: string, ingresoData: Partial<Omit<Ingreso, 'id' | 'userId' | 'createdAt'>>): Promise<void> {
    try {
      LoggerService.info('Actualizando ingreso', 'IngresoFirebaseRepository', { userId, id, ingresoData });

      const docRef = doc(db, this.collectionName, id);
      const updateData: Record<string, unknown> = {
        ...ingresoData,
        updatedAt: Timestamp.fromDate(new Date())
      };

      // Convertir fecha a Timestamp si existe
      if (ingresoData.fecha) {
        updateData.fecha = Timestamp.fromDate(ingresoData.fecha);
      }

      // Asegurar compatibilidad entre concepto y descripción
      if (ingresoData.concepto) {
        updateData.descripcion = ingresoData.descripcion || ingresoData.concepto;
      }

      await updateDoc(docRef, updateData);

      LoggerService.logUserAction('ingreso_updated', 'IngresoFirebaseRepository', { 
        userId, 
        ingresoId: id 
      });

    } catch (error) {
      LoggerService.error('Error al actualizar ingreso', 'IngresoFirebaseRepository', { 
        userId, 
        id, 
        error: error instanceof Error ? error.message : 'Error desconocido'
      });
      throw error;
    }
  }

  /**
   * Eliminar un ingreso
   */
  async deleteIngreso(userId: string, id: string): Promise<void> {
    try {
      LoggerService.info('Eliminando ingreso', 'IngresoFirebaseRepository', { userId, id });

      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);

      LoggerService.logUserAction('ingreso_deleted', 'IngresoFirebaseRepository', { 
        userId, 
        ingresoId: id 
      });

    } catch (error) {
      LoggerService.error('Error al eliminar ingreso', 'IngresoFirebaseRepository', { 
        userId, 
        id, 
        error: error instanceof Error ? error.message : 'Error desconocido'
      });
      throw error;
    }
  }

  /**
   * Migrar datos mock a Firebase (función de utilidad)
   */
  async migrateMockData(userId: string, mockIngresos: Omit<Ingreso, 'userId'>[]): Promise<void> {
    try {
      LoggerService.info('Iniciando migración de datos mock a Firebase', 'IngresoFirebaseRepository', { 
        userId, 
        count: mockIngresos.length 
      });

      const promises = mockIngresos.map(async (ingreso) => {
        const ingresoData = {
          concepto: ingreso.concepto,
          monto: ingreso.monto,
          fecha: ingreso.fecha,
          categoria: ingreso.categoria,
          descripcion: ingreso.descripcion
        };

        return this.createIngreso(userId, ingresoData);
      });

      await Promise.all(promises);

      LoggerService.logUserAction('mock_data_migrated', 'IngresoFirebaseRepository', { 
        userId, 
        type: 'ingresos',
        count: mockIngresos.length 
      });

    } catch (error) {
      LoggerService.error('Error en migración de datos mock', 'IngresoFirebaseRepository', { 
        userId, 
        error: error instanceof Error ? error.message : 'Error desconocido'
      });
      throw error;
    }
  }
}